import { mapsAdapter, MapStateType } from '@redux/maps/maps.reducer';
import { selectEngagedTargetId } from '@redux/situation/situation.selectors';
import { RootState } from '@redux/store';
import { createSelector, EntityId } from '@reduxjs/toolkit';
import { sortDate } from '@utils/date.utils';
import { isStatisticsMap } from '@utils/map/map.utils';
import { isSamePopupType } from '@utils/popup.utils';
import { chain, isEqual, uniqWith } from 'lodash';

import { SEE_POI, Zone2DTypeEnum, Zone3DTypeEnum } from '@/types/config/config.types';
import { DroneNotification, NotificationTypeEnum } from '@/types/data/data.types';
import { MapIdEnum, SensorUniqueKeys } from '@/types/map.types';
import {
  DroneControlData,
  ExpiredDroneInfoData,
  FlightPlanHistoryData,
  HistoryPopupData,
  InfoDataTypeEnum,
  PopupType,
  PopupTypeEnum,
  RoundData,
  SensorInfoData,
  TargetInfoData,
} from '@/types/popup.types';

const {
  selectEntities: selectMapEntities,
  selectAll: selectAllMaps,
  selectTotal: selectMapTotal,
} = mapsAdapter.getSelectors((state: RootState) => state.maps);

const selectMapById = mapsAdapter.getSelectors((state: RootState) => state.maps).selectById as (
  state: RootState,
  id: EntityId,
) => MapStateType | undefined;

export const MapSelectors = { selectMapEntities, selectAllMaps, selectMapTotal, selectMapById };

export const selectPopupsByMapId = createSelector(selectMapById, (state): PopupType[] => state?.popups ?? []);

export const selectSelectedTargetIdsAndDatesByMapId = createSelector(
  [selectPopupsByMapId, (state, mapId: string) => selectEngagedTargetId(state, mapId === MapIdEnum.REPLAY)],
  (popups, engagedTargetId): { targetId: string; openingDate: Date }[] => {
    const targetPopups = popups.filter(
      (popup) =>
        isSamePopupType(popup, PopupTypeEnum.INFO, InfoDataTypeEnum.INFO_TARGET) ||
        isSamePopupType(popup, PopupTypeEnum.HISTORY, NotificationTypeEnum.DRONE) ||
        isSamePopupType(popup, PopupTypeEnum.DRONE_CONTROL) ||
        isSamePopupType(popup, PopupTypeEnum.FLIGHT_PLAN_HISTORY) ||
        isSamePopupType(popup, PopupTypeEnum.ENGAGEMENT),
    );

    return chain(
      targetPopups.map((popup) => {
        if (popup.type == PopupTypeEnum.HISTORY) {
          return {
            targetId: popup.data.identifiers[0], // always contains only one targetId
            openingDate: popup.data.openingDate,
          };
        }
        if (popup.type == PopupTypeEnum.ENGAGEMENT && engagedTargetId) {
          return {
            targetId: engagedTargetId,
            openingDate: popup.data.openingDate,
          };
        }
        const data = popup.data as TargetInfoData | DroneControlData | FlightPlanHistoryData;
        return { targetId: data.targetId, openingDate: data.openingDate };
      }),
    )
      .groupBy('targetId')
      .mapValues((values) => {
        const openingDate = values
          .map((v) => v.openingDate)
          .sort(sortDate)
          .shift();
        return {
          targetId: values[0].targetId,
          openingDate,
        };
      })
      .values()
      .value() as { targetId: string; openingDate: Date }[];
  },
);

export const selectSelectedExpiredDroneNotifAndDateByMapId = createSelector(
  selectPopupsByMapId,
  (popups): { notification: DroneNotification; openingDate: Date } | null => {
    const expiredDroneInfoPopup =
      popups.find((p) => isSamePopupType(p, PopupTypeEnum.INFO, InfoDataTypeEnum.INFO_EXPIRED_DRONE)) ?? null;
    if (!expiredDroneInfoPopup) {
      return null;
    }
    const data = expiredDroneInfoPopup.data as ExpiredDroneInfoData;
    return { notification: data.notification, openingDate: data.openingDate };
  },
);

export const selectSelectedSensorsUniqueKeysByMapId = createSelector(
  selectPopupsByMapId,
  (popups): SensorUniqueKeys[] => {
    const sensorsUniqueKeysFromPopups = popups
      .filter(
        (popup) =>
          isSamePopupType(popup, PopupTypeEnum.INFO, InfoDataTypeEnum.INFO_SENSOR) ||
          isSamePopupType(popup, PopupTypeEnum.HISTORY, NotificationTypeEnum.SENSOR) ||
          isSamePopupType(popup, PopupTypeEnum.ROUND),
      )
      .map((popup) => {
        switch (popup.type) {
          case PopupTypeEnum.INFO:
            return (popup.data as SensorInfoData).featureValue;
          case PopupTypeEnum.ROUND:
            return { sensorUniqueKey: (popup.data as RoundData).featureValue };
          default:
            return (popup.data as HistoryPopupData).sensorUniqueKeys!;
        }
      });
    return uniqWith(sensorsUniqueKeysFromPopups, isEqual);
  },
);

export const selectDisplayedPerimeterLinksKeysByMapId = createSelector(
  selectMapById,
  (map) => map?.displayedPerimeterLinksKeys ?? null,
);
export const selectMapDrawModeByMapId = createSelector(selectMapById, (map) => map?.drawInfo.isDrawnOnto);
export const selectMapDrawZoneType = createSelector(selectMapById, (map) => map?.drawInfo.zoneType);
export const selectMapDrawPolygonsPoints = createSelector(selectMapById, (map) => map?.drawInfo.polygonPoints);
export const selectMapEditPolygonsInfo = createSelector(selectMapById, (map) => map?.drawInfo.editedZone);
export const selectMapNavigationOptions = createSelector(selectMapById, (map) => map?.menuNavigation);

export const selectZoneViewMode = createSelector(
  selectMapNavigationOptions,
  (selectedOptions) =>
    selectedOptions?.at(-1)?.includes('see') &&
    [...Object.values(Zone2DTypeEnum), ...Object.values(Zone3DTypeEnum)].some(
      (value) => value === selectedOptions.at(-1)?.split('_')[1],
    ),
);

export const selectPoiViewMode = createSelector(
  selectMapNavigationOptions,
  (selectedOptions) => selectedOptions?.at(-1) === SEE_POI,
);

const selectStatisticsMap = createSelector(
  (state: RootState) => state.maps.entities[MapIdEnum.STATISTICS],
  (map) => (isStatisticsMap(map) ? map : null),
);

export const selectStatisticsCriteria = createSelector(
  selectStatisticsMap,
  (statisticsMap) => statisticsMap?.criteria ?? null,
);
